'use client'

import generateClassNames from '@/utils/generate-class-names'
import clsx from 'clsx'
import mergeRefs from 'merge-refs'
import { forwardRef, useContext, useRef } from 'react'
import { TabsContext } from '../tabs-context'
import './tab-panel.scss'

const classes = generateClassNames('tab-panel', [])

export interface TabPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string
}

export const TabPanel = forwardRef(function TabPanel(
  props: TabPanelProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const { children, className, style, value, ...other } = props

  const {
    getTabId,
    getTabPanelId,
    mountOnEnter,
    tabPanelRefs,
    unmountOnExit,
    value: contextValue,
  } = useContext(TabsContext)

  const isActive = value === contextValue

  const rendered = useRef(isActive || !mountOnEnter)

  if (isActive) {
    rendered.current = true
  } else if (unmountOnExit) {
    rendered.current = false
  }

  const handleRef = (node: HTMLDivElement | null) => {
    tabPanelRefs.current[value] = node
  }

  return (
    <div
      ref={mergeRefs(handleRef, ref)}
      role='tabpanel'
      id={getTabPanelId(value)}
      aria-labelledby={getTabId(value)}
      aria-hidden={!isActive}
      className={clsx(classes.root, className)}
      style={{
        ...style,
        overflow: isActive ? 'auto' : 'hidden',
      }}
      {...other}
    >
      {rendered.current ? children : null}
    </div>
  )
})
